import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { useToast } from '../../../hooks/use-toast';
import { Loader2, Save, Image as ImageIcon, Upload } from 'lucide-react';
import BackButton from '../../../components/admin/BackButton';
import MultilingualInput from '../../../components/admin/MultilingualInput';
import ImageSuggestionModal from '../../../components/admin/ImageSuggestionModal';
import BunnyLibraryModal from '../../../components/admin/BunnyLibraryModal';
import TranslateButton from '../../../components/admin/TranslateButton';
import AutoTranslateIndicator from '../../../components/admin/AutoTranslateIndicator';
import { getHomepageSection, updateHomepageSection } from '../../../services/api';
import { uploadImage } from '../../../services/imageService';

const HeroForm = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  const [formData, setFormData] = useState({
    preheadline_pt: '',
    preheadline_en: '',
    headline_pt: '',
    headline_en: '',
    subheadline_pt: '',
    subheadline_en: '',
    trust_indicator_pt: '',
    trust_indicator_en: '',
    button_primary_text_pt: 'Agendar Reunião',
    button_primary_text_en: 'Schedule a Meeting',
    button_primary_link: '/contact',
    button_secondary_text_pt: 'Explorar Serviços',
    button_secondary_text_en: 'Explore Services',
    button_secondary_link: '/services',
    background_image: '',
    background_video: '',
    overlay_opacity: 30,
    image_brightness: 100
  });

  useEffect(() => {
    loadHeroData();
  }, []);

  const loadHeroData = async () => {
    setLoading(true);
    const result = await getHomepageSection('hero');
    
    if (result.success && result.data) {
      setFormData(prev => ({
        ...prev,
        ...result.data
      }));
    }
    
    setLoading(false);
  };

  const handleInputChange = (field) => (e) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handleImageSelect = (url) => {
    setFormData(prev => ({ ...prev, background_image: url }));
    toast({
      title: "Imagem selecionada",
      description: "A imagem foi adicionada ao formulário",
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Por favor selecione uma imagem válida (JPEG, PNG, WebP)",
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "A imagem deve ter no máximo 10MB",
      });
      return;
    }

    setUploading(true);

    try {
      const result = await uploadImage(file, 'images/hero');
      
      if (result.success) {
        setFormData(prev => ({ ...prev, background_image: result.data.cdn_url }));
        toast({
          title: "Upload completo",
          description: `Imagem enviada (${(result.data.size / 1024).toFixed(2)} KB)`,
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro no upload",
        description: error.detail || "Falha ao enviar imagem",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const result = await updateHomepageSection('hero', formData);
    
    if (result.success) {
      toast({
        title: t('common.success'),
        description: 'Hero section atualizada com sucesso',
      });
    } else {
      toast({
        title: t('common.error'),
        description: result.error || 'Erro ao guardar alterações',
        variant: 'destructive',
      });
    }
    
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#00B4D8]" />
      </div>
    );
  }

  return (
    <div>
      <BackButton to="/admin/dashboard" />
      
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-[#2B2D42]">Hero Section</h2>
        <p className="text-[#6C757D] mt-2">
          Gerir o conteúdo da seção principal da homepage (PT/EN)
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Conteúdo Hero - Multilíngue</CardTitle>
            <CardDescription>
              Preencha os campos em Português e Inglês. Use o botão "Traduzir automaticamente" para gerar traduções com GPT.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Preheadline */}
            <MultilingualInput
              type="input"
              name="preheadline"
              label="Preheadline"
              valuePT={formData.preheadline_pt}
              valueEN={formData.preheadline_en}
              onChangePT={handleInputChange('preheadline_pt')}
              onChangeEN={handleInputChange('preheadline_en')}
              placeholder={{
                pt: 'DESDE 2008 | OPERAÇÕES GLOBAIS | SOLUÇÕES EMPRESARIAIS PREMIUM',
                en: 'SINCE 2008 | GLOBAL OPERATIONS | PREMIUM BUSINESS SOLUTIONS'
              }}
              required
            />

            {/* Headline */}
            <MultilingualInput
              type="textarea"
              name="headline"
              label="Headline Principal"
              valuePT={formData.headline_pt}
              valueEN={formData.headline_en}
              onChangePT={handleInputChange('headline_pt')}
              onChangeEN={handleInputChange('headline_en')}
              rows={2}
              required
            />

            {/* Subheadline */}
            <MultilingualInput
              type="textarea"
              name="subheadline"
              label="Subheadline"
              valuePT={formData.subheadline_pt}
              valueEN={formData.subheadline_en}
              onChangePT={handleInputChange('subheadline_pt')}
              onChangeEN={handleInputChange('subheadline_en')}
              rows={3}
              required
            />

            {/* Trust Indicator */}
            <MultilingualInput
              type="input"
              name="trust_indicator"
              label="Indicador de Confiança"
              valuePT={formData.trust_indicator_pt}
              valueEN={formData.trust_indicator_en}
              onChangePT={handleInputChange('trust_indicator_pt')}
              onChangeEN={handleInputChange('trust_indicator_en')}
              placeholder={{
                pt: 'Confiado por mais de 50 empresas internacionais',
                en: 'Trusted by 50+ international companies'
              }}
            />

            {/* Primary Button */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <MultilingualInput
                type="input"
                name="button_primary_text"
                label="Botão Primário - Texto"
                valuePT={formData.button_primary_text_pt}
                valueEN={formData.button_primary_text_en}
                onChangePT={handleInputChange('button_primary_text_pt')}
                onChangeEN={handleInputChange('button_primary_text_en')}
                required
              />
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#2B2D42]">
                  Botão Primário - Link
                </label>
                <input
                  type="text"
                  value={formData.button_primary_link}
                  onChange={handleInputChange('button_primary_link')}
                  placeholder="/contact"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>

            {/* Secondary Button */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <MultilingualInput
                type="input"
                name="button_secondary_text"
                label="Botão Secundário - Texto"
                valuePT={formData.button_secondary_text_pt}
                valueEN={formData.button_secondary_text_en}
                onChangePT={handleInputChange('button_secondary_text_pt')}
                onChangeEN={handleInputChange('button_secondary_text_en')}
                required
              />
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#2B2D42]">
                  Botão Secundário - Link
                </label>
                <input
                  type="text"
                  value={formData.button_secondary_link}
                  onChange={handleInputChange('button_secondary_link')}
                  placeholder="/services"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>

            {/* Background Video (institutional loop) */}
            <div className="space-y-3 p-4 border border-[#00B4D8]/30 bg-[#00B4D8]/5 rounded-lg">
              <label className="text-sm font-semibold text-[#023E8A] flex items-center gap-2">
                🎬 Background Video <span className="text-[11px] font-normal text-gray-500">(MP4 8-15s, sem áudio, auto-loop; tem prioridade sobre a imagem)</span>
              </label>

              {formData.background_video && (
                <div className="relative rounded-lg overflow-hidden border border-gray-300 bg-black">
                  <video
                    src={formData.background_video}
                    className="w-full h-48 object-cover"
                    muted
                    loop
                    autoPlay
                    playsInline
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => setFormData((p) => ({ ...p, background_video: '' }))}
                    className="absolute top-2 right-2"
                    data-testid="remove-hero-video-btn"
                  >
                    Remover vídeo
                  </Button>
                </div>
              )}

              <div className="flex items-stretch gap-2 flex-wrap">
                <input
                  type="text"
                  value={formData.background_video || ''}
                  onChange={handleInputChange('background_video')}
                  placeholder="https://.../institutional-loop.mp4 — ou carregue um ficheiro para Bunny CDN"
                  className="flex-1 min-w-[260px] px-3 py-2 border border-gray-300 rounded-md"
                  data-testid="hero-video-url"
                />
                <input
                  type="file"
                  accept="video/mp4,video/quicktime,video/webm"
                  id="hero-video-upload-input"
                  hidden
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (f.size > 60 * 1024 * 1024) {
                      toast({ variant: 'destructive', title: 'Ficheiro demasiado grande', description: 'Máx 60 MB.' });
                      e.target.value = ''; return;
                    }
                    try {
                      setVideoUploading(true);
                      const form = new FormData();
                      form.append('file', f);
                      const r = await axios.post(`${API_URL}/api/videos/upload?folder=videos/hero`, form, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                      });
                      const cdnUrl = r.data?.data?.cdn_url;
                      if (cdnUrl) {
                        setFormData((p) => ({ ...p, background_video: cdnUrl }));
                        toast({ title: 'Vídeo carregado', description: 'Guardado no Bunny CDN.' });
                      }
                    } catch (err) {
                      toast({
                        variant: 'destructive',
                        title: 'Upload falhou',
                        description: err.response?.data?.detail || 'Erro desconhecido',
                      });
                    } finally {
                      setVideoUploading(false);
                      e.target.value = '';
                    }
                  }}
                  data-testid="hero-video-file-input"
                />
                <Button
                  type="button"
                  onClick={() => document.getElementById('hero-video-upload-input')?.click()}
                  disabled={videoUploading}
                  className="bg-[#023E8A] hover:bg-[#0353A4] text-white whitespace-nowrap"
                  data-testid="hero-video-upload-btn"
                >
                  {videoUploading ? 'A carregar...' : '📤 Upload → Bunny CDN'}
                </Button>
              </div>

              <p className="text-[11px] text-gray-500">
                Dica: um MP4 de 8-15s, H.264, 1080p sem áudio fica ~15-25 MB e carrega quase instantaneamente
                via o nosso Bunny CDN. Pode também colar um URL MP4 externo directamente no campo acima.
              </p>
            </div>

            {/* Background Image */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-[#2B2D42]">
                Background Image URL <span className="text-xs text-gray-400">(fallback quando não há vídeo)</span>
              </label>
              
              {/* Image Preview */}
              {formData.background_image && (
                <div className="relative">
                  <img
                    src={formData.background_image}
                    alt="Background preview"
                    className="w-full h-48 object-cover rounded-lg border border-gray-300"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => setFormData(prev => ({ ...prev, background_image: '' }))}
                    className="absolute top-2 right-2"
                  >
                    Remover
                  </Button>
                </div>
              )}

              {/* URL Input */}
              <input
                type="text"
                value={formData.background_image}
                onChange={handleInputChange('background_image')}
                placeholder="https://..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />

              {/* Upload Options */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                {/* Bunny.net Upload */}
                <div>
                  <input
                    type="file"
                    id="hero-file-upload"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => document.getElementById('hero-file-upload').click()}
                    disabled={uploading}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploading ? 'A enviar...' : 'Upload'}
                  </Button>
                </div>

                {/* Bunny Library (Future) */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full opacity-50 cursor-not-allowed"
                  disabled
                >
                  <ImageIcon className="w-4 h-4 mr-2" />
                  Biblioteca
                </Button>

                {/* Image Suggestions */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full border-[#00B4D8] text-[#00B4D8] hover:bg-[#E0F2FE]"
                  onClick={() => setSuggestionsOpen(true)}
                >
                  <ImageIcon className="w-4 h-4 mr-2" />
                  Sugestões
                </Button>
              </div>

              <p className="text-xs text-[#6C757D]">
                URL da imagem de fundo (opcional). Faça upload, use a biblioteca ou escolha das sugestões.
              </p>
            </div>

            {/* Image Controls — Overlay & Brightness */}
            {formData.background_image && (
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="text-sm font-bold text-[#2B2D42]">Image Controls</h4>
                
                {/* Live Preview */}
                <div className="relative h-32 rounded-lg overflow-hidden">
                  <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                      backgroundImage: `url(${formData.background_image})`,
                      filter: `brightness(${(formData.image_brightness || 100) / 100})`
                    }}
                  />
                  <div
                    className="absolute inset-0"
                    style={{ backgroundColor: `rgba(0, 0, 0, ${(formData.overlay_opacity || 30) / 100})` }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center z-10">
                    <span className="text-white font-bold text-lg drop-shadow-lg">Preview</span>
                  </div>
                </div>

                {/* Overlay Opacity */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-medium text-[#2B2D42]">
                      Overlay Darkness: {formData.overlay_opacity}%
                    </label>
                    <span className="text-xs text-[#6C757D]">0% = no overlay, 100% = full dark</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="80"
                    step="5"
                    value={formData.overlay_opacity || 30}
                    onChange={(e) => setFormData(prev => ({ ...prev, overlay_opacity: parseInt(e.target.value) }))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#00B4D8]"
                  />
                  <div className="flex justify-between text-xs text-[#6C757D] mt-1">
                    <span>Full Photo</span>
                    <span>Darker</span>
                  </div>
                </div>

                {/* Image Brightness */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-medium text-[#2B2D42]">
                      Image Brightness: {formData.image_brightness}%
                    </label>
                    <span className="text-xs text-[#6C757D]">50% = dark, 100% = normal, 150% = bright</span>
                  </div>
                  <input
                    type="range"
                    min="50"
                    max="150"
                    step="5"
                    value={formData.image_brightness || 100}
                    onChange={(e) => setFormData(prev => ({ ...prev, image_brightness: parseInt(e.target.value) }))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#FF6B35]"
                  />
                  <div className="flex justify-between text-xs text-[#6C757D] mt-1">
                    <span>Darker</span>
                    <span>Normal</span>
                    <span>Brighter</span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="mt-6 space-y-4">
          <AutoTranslateIndicator />
          <div className="flex justify-end space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin/dashboard')}
            >
              {t('common.cancel')}
            </Button>
          <Button
            type="submit"
            disabled={saving}
            className="bg-[#00B4D8] hover:bg-[#48CAE4]"
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                A guardar...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                {t('common.save')}
              </>
            )}
          </Button>
          </div>
        </div>
      </form>

      {/* Image Suggestion Modal */}
      <ImageSuggestionModal
        category="hero"
        isOpen={suggestionsOpen}
        onClose={() => setSuggestionsOpen(false)}
        onSelect={handleImageSelect}
      />

      {/* Bunny Library Modal */}
      <BunnyLibraryModal
        folder="hero"
        isOpen={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onSelect={handleImageSelect}
      />
    </div>
  );
};

export default HeroForm;
